import { Ban, Check, Circle, Clock, TimerOff, XCircle, type LucideIcon } from 'lucide-react';
import { IconBadge, Timestamp } from '@/components/ds-zta';
import { toOutcomeTone } from '@/lib/tone';
import { lifecycleSteps, type AccessRequestRecord, type LifecycleStep } from '@/features/access-requests/requestModel';

interface StatusTimelineProps {
    request: AccessRequestRecord;
    compact?: boolean;
}

/**
 * The vocabulary word each step is drawn in (src/lib/tone.ts), so a step's
 * colour is the colour that word has in every StatusBadge/OutcomeBadge. The
 * glyph and the label carry the same meaning, so colour is never alone.
 */
function stepValue(step: LifecycleStep): string {
    switch (step.kind) {
        case 'done': return 'completed';
        case 'current': return 'pending';
        case 'ended': return step.key; // expired | revoked
        case 'failed': return step.key === 'denied' ? 'denied' : 'expired';
        default: return 'unknown';
    }
}

function stepIcon(step: LifecycleStep): LucideIcon {
    if (step.kind === 'done') return Check;
    if (step.kind === 'current') return Clock;
    if (step.kind === 'failed') return step.key === 'denied' ? XCircle : TimerOff;
    if (step.kind === 'ended') return step.key === 'revoked' ? Ban : TimerOff;
    return Circle;
}

const STATE_TEXT: Record<LifecycleStep['kind'], string> = {
    done: 'done',
    current: 'in progress',
    upcoming: 'not reached',
    ended: 'ended',
    failed: 'no access granted',
};

/**
 * The path a request really took (design eval F23): pending → approved →
 * active → expired | revoked, pending → denied, and pending → expired for a
 * request nobody decided, which is not drawn as approved or provisioned.
 */
export default function StatusTimeline({ request, compact = false }: StatusTimelineProps) {
    const steps = lifecycleSteps(request);

    if (compact) {
        const summary = steps.map((s) => `${s.label} (${STATE_TEXT[s.kind]})`).join(', ');
        return (
            <ol className="flex items-center gap-1" aria-label={`Lifecycle: ${summary}`}>
                {steps.map((step, i) => {
                    const Icon = stepIcon(step);
                    const tone = toOutcomeTone(stepValue(step));
                    return (
                        <li key={step.key} className="flex items-center gap-1" aria-hidden="true">
                            <Icon data-tone={tone} className="h-3 w-3 text-[var(--tone)]" />
                            {i < steps.length - 1 && (
                                <span data-tone={tone} className="h-px w-3 bg-[var(--tone-border)]" />
                            )}
                        </li>
                    );
                })}
            </ol>
        );
    }

    return (
        <ol className="space-y-0" aria-label="Request lifecycle">
            {steps.map((step, i) => {
                const Icon = stepIcon(step);
                const tone = toOutcomeTone(stepValue(step));
                const reached = step.kind !== 'upcoming';
                const isLast = i === steps.length - 1;
                return (
                    <li key={step.key} className="flex items-start gap-3" aria-current={step.kind === 'current' ? 'step' : undefined}>
                        <div className="flex flex-col items-center self-stretch">
                            <IconBadge data-tone={tone} size={28} className="transition-all" icon={<Icon className="h-3.5 w-3.5" />} />
                            {!isLast && (
                                <div
                                    data-tone={reached ? tone : toOutcomeTone('unknown')}
                                    className="w-0.5 flex-1 min-h-6 bg-[var(--tone-border)]"
                                    aria-hidden="true"
                                />
                            )}
                        </div>
                        <div className="pt-1 pb-3 min-w-0">
                            <p
                                data-tone={tone}
                                className={reached ? 'text-sm font-medium text-[var(--tone)]' : 'text-sm font-medium text-[var(--d2d-text-muted)]'}
                            >
                                {step.label}
                                <span className="sr-only"> — {STATE_TEXT[step.kind]}</span>
                            </p>
                            {(step.by || step.at) && (
                                <p className="text-xs text-[var(--d2d-text-muted)] break-words">
                                    {step.atPrefix && <>{step.atPrefix} </>}
                                    {step.by && <>by {step.by}</>}
                                    {step.by && step.at && ' · '}
                                    {step.at && <Timestamp value={step.at} noSeconds />}
                                </p>
                            )}
                            {step.detail && (
                                <p className="text-xs text-[var(--d2d-text-muted)] break-words">{step.detail}</p>
                            )}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}
